import { useState } from "react";
import { toast } from "sonner";
import { EVIDENCE_BASED_HYPOTHESIS_STATES, todayInTz } from "@brainlog/shared";
import { errorMessage, post } from "@/lib/api";
import { useMe } from "@/lib/queries";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { NotePicker } from "./pickers";

export function StateChangeDialog(props: {
  open: boolean;
  onOpenChange(o: boolean): void;
  noteId: string;
  workspaceId: string;
  kind: string;
  field: "hypothesis_state" | "evidence_state" | "decision_status";
  labels: Record<string, string>;
  current: string | null;
  onDone(): void;
}) {
  const me = useMe();
  const [to, setTo] = useState("");
  const [date, setDate] = useState(todayInTz(me.data?.timezone));
  const [reason, setReason] = useState("");
  const [linked, setLinked] = useState<{ id: string; title: string }[]>([]);
  const [supersededBy, setSupersededBy] = useState<{ id: string; title: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const evidenceBased = props.field === "hypothesis_state" && (EVIDENCE_BASED_HYPOTHESIS_STATES as string[]).includes(to);
  const needsSuperseder = props.field === "decision_status" && to === "superseded";
  const canSave = to && to !== props.current && (!evidenceBased || linked.length > 0) && (!needsSuperseder || supersededBy);

  const submit = async () => {
    setBusy(true);
    try {
      await post(`/api/notes/${props.noteId}/record/state`, {
        field: props.field,
        to,
        effectiveDate: date || null,
        reason: reason || undefined,
        linkedNoteIds: linked.map((l) => l.id),
        supersededByNoteId: supersededBy?.id ?? null,
        expectedFrom: props.current,
      });
      toast.success("State recorded with its date and history.");
      props.onDone();
      props.onOpenChange(false);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Record a state change</DialogTitle>
          <DialogDescription>
            Current: {props.current ? props.labels[props.current] : "Not recorded"}. The change is dated and kept in history; earlier states are never deleted.
          </DialogDescription>
        </DialogHeader>
        <fieldset className="space-y-1.5">
          <legend className="text-sm font-medium">New state</legend>
          {Object.entries(props.labels).map(([v, l]) => (
            <label key={v} className="flex items-center gap-2 text-sm">
              <input type="radio" name="new-state" value={v} checked={to === v} disabled={v === props.current} onChange={() => setTo(v)} />
              {l}
              {v === props.current && <span className="text-xs text-muted-foreground">(current)</span>}
            </label>
          ))}
        </fieldset>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <Label htmlFor="sc-date">Effective date</Label>
            <Input id="sc-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
        </div>
        <div className="space-y-1">
          <Label htmlFor="sc-reason">Reason (optional)</Label>
          <Textarea id="sc-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} maxLength={5000} />
        </div>
        {needsSuperseder && (
          <div className="space-y-1">
            <Label>Later decision that supersedes this one (required)</Label>
            <NotePicker workspaceId={props.workspaceId} kinds={["decision"]} excludeIds={[props.noteId]} selected={supersededBy ? [supersededBy.id] : []} onPick={(id, title) => setSupersededBy({ id, title })} />
          </div>
        )}
        <div className="space-y-1">
          <Label>
            Records this change is based on {evidenceBased ? "(required for an evidence-based state)" : "(optional)"}
          </Label>
          {linked.length > 0 && (
            <ul className="flex flex-wrap gap-1 text-xs">
              {linked.map((l) => (
                <li key={l.id} className="rounded-full bg-secondary px-2 py-0.5">
                  {l.title}{" "}
                  <button type="button" aria-label={`Remove ${l.title}`} onClick={() => setLinked(linked.filter((x) => x.id !== l.id))}>
                    ×
                  </button>
                </li>
              ))}
            </ul>
          )}
          <NotePicker
            workspaceId={props.workspaceId}
            excludeIds={[props.noteId]}
            multiple
            selected={linked.map((l) => l.id)}
            onPick={(id, title) => setLinked(linked.some((l) => l.id === id) ? linked.filter((l) => l.id !== id) : [...linked, { id, title }])}
          />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => props.onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!canSave || busy}>
            {busy ? "Saving…" : "Record change"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
